import {
    Button, Flex, Form, theme,
} from 'antd';
import {
    type CSSProperties, type LegacyRef, type RefObject,
    useCallback, useEffect, useLayoutEffect,
} from 'react';
import {
    Link,
    type To,
} from 'react-router-dom';

import { useSetFloatingFormSaveRegistration } from '@/contexts/FloatingFormSaveContext';

import { getActionControlStyleVars } from './actionControlStyles';

type FormPrimaryActionsRowProps = {
    onCancel?: () => void;
    cancelButtonText?: string;
    cancelTo?: To;
    loading: boolean;
    submitDisabled: boolean;
    submitLabel: string;
    style?: CSSProperties;
};

export const FormPrimaryActionsRow = ({
    onCancel,
    cancelButtonText,
    cancelTo,
    loading,
    submitDisabled,
    submitLabel,
    style,
}: FormPrimaryActionsRowProps) => {
    const { token } = theme.useToken();
    const cancelStyleVars = getActionControlStyleVars(token, {
        variant: 'default',
        bordered: true,
        disabled: loading,
    });

    return (
        <Form.Item style={{ marginBottom: 0, ...style }}>
            <Flex gap="middle" justify="end">
                {cancelTo && !loading ? (
                    <Link
                        to={cancelTo}
                        className="app-action-control"
                        style={cancelStyleVars}
                    >
                        {cancelButtonText || 'Назад'}
                    </Link>
                ) : onCancel && (
                    <Button
                        className="app-action-control"
                        style={cancelStyleVars}
                        onClick={onCancel}
                        disabled={loading}
                    >
                        {cancelButtonText || 'Назад'}
                    </Button>
                )}
                <Button
                    type="primary"
                    htmlType="submit"
                    loading={loading}
                    disabled={submitDisabled}
                >
                    {submitLabel}
                </Button>
            </Flex>
        </Form.Item>
    );
};

type FormSaveFloatButtonProps = {
    formDomId: string;
    hiddenSubmitRef: RefObject<HTMLButtonElement | null>;
    loading: boolean;
    submitDisabled: boolean;
    submitLabel: string;
};

export const FormSaveFloatButton = ({
    formDomId,
    hiddenSubmitRef,
    loading,
    submitDisabled,
    submitLabel,
}: FormSaveFloatButtonProps) => {
    const setRegistration = useSetFloatingFormSaveRegistration();

    const requestSubmit = useCallback(() => {
        if (!submitDisabled && !loading) {
            hiddenSubmitRef.current?.click();
        }
    }, [submitDisabled, loading, hiddenSubmitRef]);

    useLayoutEffect(() => {
        if (!setRegistration) return;
        setRegistration({
            submitLabel,
            submitDisabled,
            loading,
            requestSubmit,
        });
    }, [setRegistration, submitLabel, submitDisabled, loading, requestSubmit]);

    useEffect(() => () => {
        setRegistration?.(null);
    }, [setRegistration]);

    return (
        <button
            ref={hiddenSubmitRef as LegacyRef<HTMLButtonElement>}
            type="submit"
            form={formDomId}
            disabled={submitDisabled}
            tabIndex={-1}
            aria-hidden
            style={{
                position: 'absolute',
                width: 0,
                height: 0,
                opacity: 0,
                pointerEvents: 'none',
                border: 0,
                padding: 0,
            }}
        />
    );
};
